import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  HeartPulse, Users, MapPin, CalendarCheck, Clock, FileCheck2, BarChart3,
  Check, Sparkles, ArrowRight, Layers, Nfc,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, delay: d },
});

const PILLAR_ICONS = [Users, HeartPulse, MapPin, CalendarCheck, Clock, FileCheck2, BarChart3];
const PILLAR_READY = [true, true, true, true, true, true, true];
const ARCH_CLS = [
  "bg-navy text-white border-navy",
  "bg-gradient-to-r from-cyan-dark to-teal-600 text-white border-cyan",
  "bg-slate-900 text-cyan border-cyan/40",
];
const FLOW_ICONS = [Users, HeartPulse, CalendarCheck, MapPin, CalendarCheck, Nfc, Clock, MapPin, BarChart3];
const TOUR_ROW_CLS = ["bg-blue-50 text-blue-600", "bg-cyan/10 text-cyan-dark", "bg-cyan/10 text-cyan-dark"];
const TOUR_ROW_TASKS = [4, 3, 5];
const TE_TIMELINE_CLS = ["bg-navy text-white", "bg-cyan/10 text-cyan-dark", "bg-amber-50 text-amber-700", "bg-cyan/10 text-cyan-dark", "bg-slate-100 text-slate-500", "bg-navy text-white"];

export function PflegeSection() {
  const { t } = useTranslation();
  const p = (k, opts) => t(`pflegeSec.${k}`, opts);
  const arr = (k) => t(`pflegeSec.${k}`, { returnObjects: true });

  const pillars = arr("pillars");
  const arch = arr("arch");
  const careFeatures = arr("careFeatures");
  const bundleFeatures = arr("bundleFeatures");
  const flowNodes = arr("flowNodes");
  const patFeatures = arr("patFeatures");
  const verChain = arr("verChain");
  const verChips = arr("verChips");
  const tourChips = arr("tourChips");
  const tourChainItems = arr("tourChainItems");
  const tourRows = arr("tourRows");
  const nfcChain = arr("nfcChain");
  const teChips = arr("teChips");
  const teTimeline = arr("teTimeline");
  const teStats = arr("teStats");
  const repChips = arr("repChips");
  const repChain = arr("repChain");
  const repStats = arr("repStats");

  return (
    <section id="pflege" className="relative overflow-hidden border-t border-slate-200/60 bg-gradient-to-b from-slate-50 via-white to-slate-50 py-24" data-testid="pflege-section">
      <div className="mx-auto max-w-7xl px-5">
        {/* header */}
        <motion.div {...fade(0)} className="max-w-3xl">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-cyan-dark">
            <HeartPulse className="h-3.5 w-3.5" /> {p("badge")}
          </div>
          <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">{p("h2")}</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">{p("intro")}</p>
        </motion.div>

        {/* flagship end-to-end chain (Phase 12 §54) */}
        <motion.div {...fade(0.06)} className="mt-8 rounded-3xl border border-slate-200 bg-navy p-6 sm:p-8" data-testid="pflege-flagship-flow">
          <div className="text-[11px] font-bold uppercase tracking-wider text-cyan">{p("flowLabel")}</div>
          <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-3">
            {flowNodes.map((label, i, a) => {
              const Icon = FLOW_ICONS[i];
              return (
                <div key={label} className="flex items-center gap-2">
                  <div className={`flex items-center gap-2 rounded-xl px-3 py-2 ${i === a.length - 1 ? "bg-gradient-to-r from-cyan-dark to-teal-500 text-white" : "bg-white/10 text-white"}`} data-testid={`flagship-node-${i}`}>
                    <Icon className="h-4 w-4 text-cyan" strokeWidth={2} />
                    <span className="text-xs font-bold sm:text-sm">{label}</span>
                  </div>
                  {i < a.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 text-white/30" />}
                </div>
              );
            })}
          </div>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-slate-300">{p("flowDesc")}</p>
        </motion.div>


        {/* pillars */}
        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7" data-testid="pflege-pillars">
          {pillars.map(([title, desc], i) => {
            const Icon = PILLAR_ICONS[i];
            const ready = PILLAR_READY[i];
            return (
              <motion.div key={title} {...fade(0.03 * i)} data-testid={`pflege-pillar-${i}`}
                className="rounded-2xl border border-slate-200 bg-white p-4 transition-transform hover:-translate-y-1">
                <Icon className="h-5 w-5 text-cyan-dark" strokeWidth={1.75} />
                <div className="mt-2 text-sm font-bold text-navy">{title}</div>
                <div className="mt-0.5 text-xs text-slate-500">{desc}</div>
                <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${ready ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-700"}`}>{ready ? p("active") : p("inPrep")}</span>
              </motion.div>
            );
          })}
        </div>

        {/* architecture */}
        <motion.div {...fade(0.1)} className="mt-16" data-testid="pflege-arch-diagram">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-cyan-dark"><Layers className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">{p("archLabel")}</span></div>
            <p className="mt-2 text-sm text-slate-600">{p("archDesc")}</p>
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            {arch.map(([level, title, desc], i) => (
              <div key={title} className={`rounded-2xl border p-5 ${ARCH_CLS[i]}`}>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70">{level}</div>
                <div className="mt-1 font-heading text-lg font-extrabold">{title}</div>
                <div className="mt-1.5 text-xs opacity-85">{desc}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* pricing cards */}
        <div className="mt-16">
          <motion.div {...fade(0)} className="max-w-2xl">
            <h3 className="font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">{p("pricingTitle")}</h3>
            <p className="mt-2 text-sm text-slate-600">{p("pricingDesc")}</p>
          </motion.div>
          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            {/* Pflege Modul */}
            <motion.div {...fade(0.05)} data-testid="pflege-card-pflege-modul"
              className="flex flex-col rounded-3xl border border-slate-200 bg-white p-7">
              <span className="inline-flex w-fit items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">{p("modulBadge")}</span>
              <div className="mt-4 font-heading text-xl font-extrabold text-navy">{p("modulName")}</div>
              <div className="mt-2 flex items-end gap-1"><span className="font-heading text-4xl font-extrabold text-navy">3.500 €</span><span className="mb-1 text-sm text-slate-500">{p("perYear")}</span></div>
              <div className="text-xs text-slate-400">{p("plusVat")}</div>
              <ul className="mt-5 flex-1 space-y-2">
                {careFeatures.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-dark" /> {f}</li>)}
              </ul>
              <Link to="/register" className="mt-6"><Button variant="outline" className="w-full rounded-full border-navy/20 text-navy hover:bg-navy hover:text-white" data-testid="pflege-cta-modul">{p("ctaModul")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
            </motion.div>

            {/* Pflege Komplett (recommended) */}
            <motion.div {...fade(0.1)} data-testid="pflege-card-pflege-komplett"
              className="relative flex flex-col rounded-3xl border-2 border-cyan bg-white p-7 shadow-[0_24px_70px_-30px_rgba(6,182,212,0.55)]">
              <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-dark to-teal-600 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white"><Sparkles className="h-3 w-3" /> {p("komplettBadge")}</span>
              <div className="mt-3 font-heading text-xl font-extrabold text-navy">{p("komplettName")}</div>
              <div className="mt-2 flex items-end gap-2">
                <span className="font-heading text-4xl font-extrabold text-navy">5.000 €</span><span className="mb-1 text-sm text-slate-500">{p("perYear")}</span>
                <span className="mb-1 text-sm text-slate-400 line-through">6.000 €</span>
              </div>
              <div className="text-xs text-slate-400">{p("plusVat")}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700"><Sparkles className="h-3 w-3" /> {p("save1000")}</span>
                <span className="inline-flex items-center rounded-full bg-cyan/10 px-2.5 py-1 text-[11px] font-bold text-cyan-dark">{p("cheaper")}</span>
              </div>
              <ul className="mt-5 flex-1 space-y-2">
                {bundleFeatures.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>)}
              </ul>
              <Link to="/register" className="mt-6"><Button className="w-full rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="pflege-cta-komplett">{p("ctaKomplett")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
            </motion.div>
          </div>
        </div>

        {/* price comparison */}
        <motion.div {...fade(0.05)} className="mt-10 rounded-3xl border border-slate-200 bg-white p-7" data-testid="pflege-price-comparison">
          <h3 className="font-heading text-xl font-extrabold text-navy">{p("compTitle")}</h3>
          <div className="mt-5 grid items-center gap-5 md:grid-cols-[1fr_auto_1fr]">
            <div className="rounded-2xl bg-slate-50 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{p("compSingle")}</div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                <div className="flex justify-between"><span>{p("compModulRow")}</span><span className="font-semibold">3.500 €</span></div>
                <div className="flex justify-between"><span>{p("compZeitRow")}</span><span className="font-semibold">2.500 €</span></div>
                <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 font-heading font-extrabold text-navy"><span>{p("compTotal")}</span><span>{p("compTotalVal")}</span></div>
              </div>
            </div>
            <div className="mx-auto text-slate-300"><ArrowRight className="hidden h-6 w-6 md:block" /></div>
            <div className="rounded-2xl border-2 border-cyan/40 bg-cyan/5 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-cyan-dark">{p("compBundleLabel")}</div>
              <div className="mt-3 flex items-end gap-2"><span className="font-heading text-3xl font-extrabold text-navy">5.000 €</span><span className="mb-1 text-sm text-slate-500">{p("perYear")}</span></div>
              <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white"><Sparkles className="h-3.5 w-3.5" /> {p("compSaveBadge")}</div>
              <div className="mt-2 text-xs text-slate-500">{p("compBundleNote")}</div>
            </div>
          </div>
        </motion.div>
        {/* patient management (Phase 6) */}
        <motion.div {...fade(0.05)} className="mt-16 grid items-center gap-6 rounded-3xl border border-slate-200 bg-white p-7 lg:grid-cols-2" data-testid="pflege-patient-id">
          <div>
            <div className="flex items-center gap-2 text-cyan-dark"><Users className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">{p("patLabel")}</span></div>
            <h3 className="mt-2 font-heading text-2xl font-extrabold text-navy">{p("patTitle")}</h3>
            <p className="mt-2 text-sm text-slate-600">{p("patDesc")}</p>
            <ul className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
              {patFeatures.map((x) => (
                <li key={x} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-dark" /> {x}</li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-slate-400">{p("patNote")}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{p("patCardLabel")}</div>
            <div className="mt-2 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-cyan/10 text-cyan-dark"><HeartPulse className="h-6 w-6" /></div>
              <div>
                <div className="font-heading font-bold text-navy">Erika M.</div>
                <div className="font-mono text-sm text-cyan-dark">CARE_PFLEGEMOBIL_PATIENT_0042</div>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-500">{p("patCardNote")}</p>
          </div>
        </motion.div>
        {/* clinical versioning (Phase 7) */}
        <motion.div {...fade(0.05)} className="mt-8 rounded-3xl border border-slate-200 bg-white p-7" data-testid="pflege-versioning">
          <div className="flex items-center gap-2 text-cyan-dark"><Layers className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">{p("verLabel")}</span></div>
          <h3 className="mt-2 font-heading text-2xl font-extrabold text-navy">{p("verTitle")}</h3>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">{p("verDesc")}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
            <span className="rounded-xl bg-navy px-4 py-2 font-semibold text-white">{verChain[0]}</span>
            <ArrowRight className="h-4 w-4 text-slate-300" />
            <span className="rounded-xl bg-slate-100 px-4 py-2 font-semibold text-slate-500">{verChain[1]}</span>
            <ArrowRight className="h-4 w-4 text-slate-300" />
            <span className="rounded-xl bg-gradient-to-r from-cyan-dark to-teal-600 px-4 py-2 font-semibold text-white">{verChain[2]}</span>
            <ArrowRight className="h-4 w-4 text-slate-300" />
            <span className="rounded-xl border-2 border-cyan/40 bg-cyan/5 px-4 py-2 font-semibold text-cyan-dark">{verChain[3]}</span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {verChips.map((x) => (
              <span key={x} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{x}</span>
            ))}
          </div>
        </motion.div>

        {/* tour planning (Phase 8) */}
        <motion.div {...fade(0.05)} className="mt-8 grid items-center gap-6 rounded-3xl border border-slate-200 bg-white p-7 lg:grid-cols-[1.1fr_1fr]" data-testid="pflege-tours">
          <div>
            <div className="flex items-center gap-2 text-cyan-dark"><MapPin className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">{p("tourLabel")}</span></div>
            <h3 className="mt-2 font-heading text-2xl font-extrabold text-navy">{p("tourTitle")}</h3>
            <p className="mt-2 text-sm text-slate-600">{p("tourDesc")}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {tourChips.map((x) => (
                <span key={x} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{x}</span>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs font-semibold">
              {tourChainItems.map((it, i, a) => (
                <span key={it} className="flex items-center gap-2">
                  <span className={`rounded-lg px-3 py-1.5 ${i === 0 ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{it}</span>
                  {i < a.length - 1 && <span className="text-slate-300">+</span>}
                </span>
              ))}
              <ArrowRight className="h-4 w-4 text-slate-300" /><span className="rounded-lg bg-gradient-to-r from-cyan-dark to-teal-600 px-3 py-1.5 text-white">{p("tourChainEnd")}</span>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="pflege-tour-visual">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><div className="flex h-9 w-9 items-center justify-center rounded-xl bg-navy text-white"><Users className="h-4.5 w-4.5" /></div><div><div className="text-sm font-bold text-navy">{p("tourWorker")}</div><div className="text-[11px] text-slate-400">{p("tourTime")}</div></div></div>
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">{p("tourReady")}</span>
            </div>
            <div className="mt-4 space-y-2">
              {tourRows.map(([time, pt, st], i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl bg-white p-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-cyan/10 text-xs font-bold text-cyan-dark">{i + 1}</div>
                  <div className="flex-1"><div className="text-sm font-semibold text-navy">{pt}</div><div className="text-[11px] text-slate-400">{time} · {TOUR_ROW_TASKS[i]} {p("tourTasksSuffix")}</div></div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${TOUR_ROW_CLS[i]}`}>{st}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* NFC / QR (Phase 9) */}
        <motion.div {...fade(0.05)} className="mt-8" data-testid="pflege-nfc">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-cyan-dark"><Nfc className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">{p("nfcLabel")}</span></div>
            <h3 className="mt-2 font-heading text-2xl font-extrabold text-navy">{p("nfcTitle")}</h3>
            <p className="mt-2 text-sm text-slate-600">{p("nfcDesc")}</p>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="pflege-nfc-time">
              <div className="flex items-center gap-2 text-navy"><Clock className="h-5 w-5 text-cyan-dark" /><span className="font-heading font-extrabold">{p("nfcTimeTitle")}</span></div>
              <p className="mt-2 text-sm text-slate-600">{p("nfcTimeA")}<span className="font-semibold">{p("nfcTimeBold")}</span>{p("nfcTimeB")}</p>
              <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">EG-CARE-TIME-000184</div>
            </div>
            <div className="rounded-2xl border-2 border-cyan/40 bg-cyan/5 p-6" data-testid="pflege-nfc-visit">
              <div className="flex items-center gap-2 text-navy"><HeartPulse className="h-5 w-5 text-cyan-dark" /><span className="font-heading font-extrabold">{p("nfcVisitTitle")}</span></div>
              <p className="mt-2 text-sm text-slate-600">{p("nfcVisitA")}<span className="font-semibold">{p("nfcVisitBold")}</span>{p("nfcVisitB")}</p>
              <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-cyan-dark">EG-CARE-PATIENT-001928</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm font-semibold">
            {nfcChain.map((it, i, a) => (
              <span key={it} className="flex items-center gap-2">
                <span className={`rounded-lg px-3 py-1.5 ${i === 0 ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{it}</span>
                {i < a.length - 1 && <span className="text-slate-300">+</span>}
              </span>
            ))}
            <ArrowRight className="h-4 w-4 text-slate-300" /><span className="rounded-lg bg-gradient-to-r from-cyan-dark to-teal-600 px-3 py-1.5 text-white">{p("nfcChainEnd")}</span>
          </div>
          <p className="mt-4 text-xs text-slate-400">{p("nfcNote")}</p>
        </motion.div>

        {/* time + travel engine (Phase 10) */}
        <motion.div {...fade(0.05)} className="mt-8 grid items-start gap-6 rounded-3xl border border-slate-200 bg-white p-7 lg:grid-cols-[1fr_1.1fr]" data-testid="pflege-timeengine">
          <div>
            <div className="flex items-center gap-2 text-cyan-dark"><Clock className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">{p("teLabel")}</span></div>
            <h3 className="mt-2 font-heading text-2xl font-extrabold text-navy">{p("teTitle")}</h3>
            <p className="mt-2 text-sm text-slate-600">{p("teDesc")}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {teChips.map((x) => (
                <span key={x} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{x}</span>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-400">{p("teNote")}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="pflege-timeline-visual">
            <div className="space-y-2">
              {teTimeline.map(([tm, l], i) => (
                <div key={i} className="flex items-center gap-3"><span className="w-24 shrink-0 text-[11px] font-mono text-slate-400">{tm}</span><span className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold ${TE_TIMELINE_CLS[i]}`}>{l}</span></div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 border-t border-slate-200 pt-3 text-center">
              {teStats.map(([k, v]) => (
                <div key={k}><div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div><div className="mt-0.5 font-heading text-sm font-extrabold text-navy">{v}</div></div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Phase 11: monthly reporting story */}
        <motion.div {...fade(0.05)} className="mt-8 rounded-3xl border border-slate-200 bg-white p-7" data-testid="pflege-reporting">
          <div className="flex items-center gap-2 text-cyan-dark"><BarChart3 className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">{p("repLabel")}</span></div>
          <h3 className="mt-2 font-heading text-2xl font-extrabold text-navy">{p("repTitle")}</h3>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">{p("repDesc")}</p>
          <div className="mt-5 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <div className="flex flex-wrap gap-2">
                {repChips.map((x) => (
                  <span key={x} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600"><Check className="h-3 w-3 text-cyan-dark" />{x}</span>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-2 text-xs font-semibold">
                {repChain.map((s, i, a) => (
                  <span key={s} className="flex items-center gap-2">
                    <span className={`rounded-lg px-3 py-1.5 ${i === a.length - 1 ? "bg-gradient-to-r from-cyan-dark to-teal-600 text-white" : "bg-navy text-white"}`}>{s}</span>
                    {i < a.length - 1 && <ArrowRight className="h-4 w-4 text-slate-300" />}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="pflege-report-visual">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{p("repVisualLabel")}</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {repStats.map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div><div className="mt-0.5 font-heading text-xl font-extrabold text-navy">{v}</div></div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-navy shadow-sm"><FileCheck2 className="h-3.5 w-3.5 text-cyan-dark" /> PDF</span>
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-navy shadow-sm"><FileCheck2 className="h-3.5 w-3.5 text-emerald-600" /> Excel</span>
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-navy shadow-sm"><FileCheck2 className="h-3.5 w-3.5 text-slate-400" /> CSV</span>
              </div>
              <p className="mt-3 text-[11px] text-slate-400">{p("repNote")}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
